// Case-study data. Each project has EN + KO content for detail pages.
export const PROJECTS = [
  {
    code: "CS-241",
    year: "2025",
    duration: "14 months",
    duration_ko: "14개월",
    cohort: "32 surgeons · 3 sites",
    cohort_ko: "외과의 32명 · 3개 사이트",
    domain_key: "robotics",
    domain_en: "Surgical Robotics",
    domain_ko: "수술 로보틱스",
    metric: "−23%",
    metric_label_en: "Surgeon Fatigue (EMG)",
    metric_label_ko: "외과의 피로도 (EMG)",
    img: "https://images.unsplash.com/photo-1776104501583-3c83f2adfc3d?auto=format&fit=crop&w=1600&q=80",
    hero_img: "https://images.unsplash.com/photo-1776104501583-3c83f2adfc3d?auto=format&fit=crop&w=2400&q=80",
    en: {
      title: "Console Ergonomics for Bimanual Telesurgery",
      blurb:
        "A 14-month evaluation of a next-gen surgical robot console. Eye-tracking + EMG identified a 23% reduction in fatigue after re-mapped foot pedal logic.",
      overview:
        "We partnered with a leading surgical robotics OEM to evaluate the ergonomics of their next-generation telesurgery console across complex multi-hour procedures. The goal: quantify surgeon fatigue and identify the specific interaction patterns that drive it.",
      challenge:
        "Multi-hour bimanual telesurgery imposes sustained cognitive and muscular load. Surgeons reported neck and forearm fatigue but root-cause was unclear — was it the foot pedals, the camera arm logic, the view angle, or the haptic gain?",
      approach:
        "Mixed-method study across three teaching hospitals. We combined eye-tracking (Tobii Pro Spectrum 1200Hz), surface EMG on trapezius and forearm extensors, NASA-TLX after each procedure, and structured debriefs. Procedures were instrumented with synchronised video and console telemetry.",
      findings: [
        "Foot pedal logic accounted for 41% of forearm bracing micro-movements.",
        "Re-mapping the camera pedal to a dedicated rocker reduced trapezius EMG by 23% over a 90-minute task.",
        "Surgeons' subjective fatigue ratings (NASA-TLX) dropped 18 points on average.",
        "Trainee surgeons benefited 2.4× more than experts from the re-mapping.",
      ],
      methods_used: ["Gaze Analytics", "Psychophysiology (EMG)", "NASA-TLX", "Telemetry Sync", "Summative Validation"],
    },
    ko: {
      title: "양손 원격수술용 콘솔 인간공학 평가",
      blurb:
        "차세대 수술 로봇 콘솔의 14개월 평가. 아이트래킹과 EMG를 통해 풋페달 로직 재매핑 후 피로도가 23% 감소함을 입증.",
      overview:
        "선도적인 수술 로봇 OEM과 협력하여 복잡한 다시간 시술 환경에서 차세대 원격수술 콘솔의 인간공학적 특성을 평가했습니다. 목표는 외과의 피로도를 정량화하고 이를 유발하는 구체적 상호작용 패턴을 식별하는 것이었습니다.",
      challenge:
        "수 시간에 걸친 양손 원격수술은 지속적인 인지·근육 부하를 부과합니다. 외과의들은 목과 전완 피로를 호소했으나 근본 원인은 불분명했습니다 — 풋페달인가, 카메라 암 로직인가, 시야각인가, 햅틱 게인인가?",
      approach:
        "3개 교육병원에 걸친 혼합 방법 연구를 수행했습니다. 아이트래킹(Tobii Pro Spectrum 1200Hz), 승모근·전완근 표면 EMG, 시술 종료 후 NASA-TLX, 구조화된 디브리프를 결합했습니다. 시술은 동기화된 영상과 콘솔 텔레메트리로 계측되었습니다.",
      findings: [
        "풋페달 로직이 전완 긴장 미세동작의 41%를 차지함.",
        "카메라 페달을 전용 로커로 재매핑한 결과, 90분 과업 동안 승모근 EMG가 23% 감소.",
        "외과의 주관적 피로도(NASA-TLX)는 평균 18점 하락.",
        "수련의는 숙련의보다 재매핑의 이점을 2.4배 더 크게 경험.",
      ],
      methods_used: ["아이트래킹", "생체신호(EMG)", "NASA-TLX", "텔레메트리 동기화", "총괄 검증"],
    },
  },
  {
    code: "AI-118",
    year: "2024",
    duration: "9 months",
    duration_ko: "9개월",
    cohort: "48 radiologists",
    cohort_ko: "영상의학과 전문의 48명",
    domain_key: "ai",
    domain_en: "Clinical AI",
    domain_ko: "임상 AI",
    metric: "+41%",
    metric_label_en: "Appropriate Reliance",
    metric_label_ko: "적절한 신뢰 의존도",
    img: "https://images.unsplash.com/photo-1631651363531-fd29aec4cb5c?auto=format&fit=crop&w=1600&q=80",
    hero_img: "https://images.unsplash.com/photo-1631651363531-fd29aec4cb5c?auto=format&fit=crop&w=2400&q=80",
    en: {
      title: "Calibrating Trust in Diagnostic Copilots",
      blurb:
        "How radiologists interpret confidence scores from a CT lesion-detection model. Findings informed a redesigned uncertainty UI now in regulatory submission.",
      overview:
        "A leading diagnostic AI vendor wanted to ship a lesion-detection copilot for chest CT, but pilot deployments showed worrying patterns: radiologists either over-trusted high-confidence predictions or wholesale ignored the system. We were brought in to characterise — and fix — the trust calibration problem.",
      challenge:
        "Confidence scores expressed as percentages were systematically misinterpreted. Radiologists treated 85% confidence as 'definitely there' and 60% as 'almost certainly not.' This binarisation defeated the calibration the model team had carefully built.",
      approach:
        "We ran a within-subject study with 48 board-certified radiologists across two countries. Each read 32 CT scans under three UI conditions: baseline (percentage), redesigned (calibrated visual language + counterfactual examples), and control (no AI). Eye-tracking captured attention to AI cues; verbal protocols captured reasoning.",
      findings: [
        "Baseline UI produced 'lazy reliance' on 28% of cases — radiologists agreed with the AI without examining the scan.",
        "Redesigned UI cut lazy reliance to 11% (−61%) without reducing total agreement rate.",
        "Appropriate reliance (agreement when AI correct, disagreement when AI wrong) rose by 41%.",
        "Counterfactual examples were the single highest-impact design element.",
      ],
      methods_used: ["Gaze Analytics", "Verbal Protocol", "Within-Subject Trial", "Trust Calibration"],
    },
    ko: {
      title: "진단 보조 AI의 신뢰 보정",
      blurb:
        "흉부 CT 병변 검출 모델의 신뢰도 점수를 영상의학과 전문의가 어떻게 해석하는지 연구. 그 결과를 반영한 불확실성 UI는 현재 인허가 단계에 있습니다.",
      overview:
        "주요 진단 AI 기업이 흉부 CT용 병변 검출 보조 시스템을 출시하려 했으나, 시범 운영에서 우려스러운 패턴이 관찰됐습니다 — 영상의학과 전문의들이 높은 신뢰도 예측을 과신하거나, 시스템 전체를 무시하는 양극화 현상이었습니다. CATT는 신뢰 보정 문제를 특성화하고 개선하기 위해 합류했습니다.",
      challenge:
        "백분율로 표시된 신뢰도 점수는 체계적으로 잘못 해석되었습니다. 영상의들은 85%를 '확실히 있음'으로, 60%를 '거의 확실히 없음'으로 받아들였습니다. 이 이진화는 모델팀이 정교하게 구축한 보정을 무력화시켰습니다.",
      approach:
        "2개국 영상의학과 전문의 48명을 대상으로 피험자 내 설계 연구를 수행했습니다. 각자 3가지 UI 조건 하에서 32개 CT 영상을 판독했습니다: 기준(백분율), 재설계(보정된 시각 언어 + 반사실 예시), 통제(AI 없음). 아이트래킹으로 AI 단서에 대한 주의를, 발화 프로토콜로 추론 과정을 포착했습니다.",
      findings: [
        "기준 UI는 28%의 사례에서 '게으른 의존'을 유발 — 영상의들이 영상을 직접 확인하지 않고 AI에 동의.",
        "재설계 UI는 게으른 의존을 11%로 감소(-61%)시키면서 전체 동의율은 유지.",
        "적절한 신뢰 의존도(AI가 옳을 때 동의, 틀릴 때 불일치)는 41% 상승.",
        "반사실 예시가 단일 요소로서 가장 큰 영향력을 보였습니다.",
      ],
      methods_used: ["아이트래킹", "발화 프로토콜", "피험자 내 시험", "신뢰 보정"],
    },
  },
  {
    code: "MD-307",
    year: "2024",
    duration: "7 months",
    duration_ko: "7개월",
    cohort: "25 ICU nurses",
    cohort_ko: "중환자실 간호사 25명",
    domain_key: "medical",
    domain_en: "Medical Device",
    domain_ko: "의료기기",
    metric: "0",
    metric_label_en: "Critical Use-Errors",
    metric_label_ko: "치명적 사용 오류",
    img: "https://images.pexels.com/photos/6129679/pexels-photo-6129679.jpeg",
    hero_img: "https://images.pexels.com/photos/6129679/pexels-photo-6129679.jpeg",
    en: {
      title: "Summative HFE for Smart Infusion Pumps",
      blurb:
        "IEC 62366-1 aligned summative study with 25 ICU nurses across three sites. Eliminated two critical use-errors before FDA submission.",
      overview:
        "A Tier-1 medical device manufacturer required summative human factors validation of their next-generation smart infusion pump, including drug library and bidirectional EHR integration, prior to 510(k) submission.",
      challenge:
        "Smart pumps consolidate dozens of clinical workflows behind a single screen. Two critical-task patterns had been flagged during formative testing as failure-prone: emergency override during code blue, and bolus reprogramming mid-infusion. FDA HFE guidance required documented summative evidence of mitigation.",
      approach:
        "We executed an IEC 62366-1 aligned summative study with 25 ICU nurses (representative of intended user population) across three simulated environments: simulation lab, mock ICU bay, and a fully instrumented high-fidelity simulator. Each participant performed 12 critical tasks with use-error scoring against the manufacturer's URRA.",
      findings: [
        "Zero critical use-errors observed across 300 task instances.",
        "Two close-call patterns identified and resolved through label and flow changes.",
        "Time-to-bolus reduced by 31% vs predecessor device.",
        "Submission package accepted by FDA with no HFE deficiencies.",
      ],
      methods_used: ["Summative Validation", "Task & Error Analysis", "URRA Mapping", "Simulation"],
    },
    ko: {
      title: "스마트 수액 펌프 총괄 HFE 평가",
      blurb:
        "3개 사이트의 중환자실 간호사 25명을 대상으로 한 IEC 62366-1 부합 총괄 연구. FDA 제출 전 두 가지 치명적 사용 오류를 제거했습니다.",
      overview:
        "1등급 의료기기 제조사가 510(k) 제출에 앞서 약물 라이브러리와 양방향 EHR 연동 기능을 포함한 차세대 스마트 수액 펌프의 총괄 인간요인 검증을 의뢰했습니다.",
      challenge:
        "스마트 펌프는 수십 가지 임상 워크플로우를 단일 화면 뒤로 통합합니다. 형성 평가 단계에서 두 가지 치명적 과업 패턴이 실패 위험으로 표시되었습니다: 코드 블루 중 비상 오버라이드, 그리고 주입 중 볼러스 재프로그래밍. FDA HFE 가이드는 완화 조치에 대한 문서화된 총괄 근거를 요구합니다.",
      approach:
        "3개 모의 환경(시뮬레이션 랩, 모의 ICU 베이, 완전 계측 고충실도 시뮬레이터)에서 25명의 중환자실 간호사를 대상으로 IEC 62366-1 부합 총괄 연구를 수행했습니다. 각 참가자는 제조사의 URRA에 따른 사용 오류 평가와 함께 12개의 치명적 과업을 수행했습니다.",
      findings: [
        "300개 과업 사례에서 치명적 사용 오류 0건.",
        "두 가지 근접 사고 패턴 식별 및 라벨·흐름 변경으로 해결.",
        "이전 기기 대비 볼러스 투여 시간 31% 단축.",
        "FDA 제출 패키지가 HFE 결함 없이 승인.",
      ],
      methods_used: ["총괄 검증", "과업·오류 분석", "URRA 매핑", "시뮬레이션"],
    },
  },
];

export const findProject = (code) => PROJECTS.find((p) => p.code === code);
